import { useContext, useEffect, useRef } from 'react'
import CurrencyContext from 'contexts/currencyContext'
import { useAppSelector } from 'hooks/reduxHooks'
import noop from 'lib/function/noop'
import { isGhaVerifierUserAgent } from 'lib/metasearch/ghaLoadingDiagnostics'
import { getMetasearchPriceBreakdownCheck, type MetasearchPriceBreakdown } from 'lib/metasearch/metasearchPriceBreakdownCheck'
import { generalLog } from 'marketing/api'
import { isGhaRequest } from 'lib/url/ghaRequestUtils'

interface Options extends MetasearchPriceBreakdown {
  ready: boolean
  surface: 'metasearch' | 'spp' | 'legacy_bedbank'
}

/** Runs on settled pricing, independently of whether the guest opens the price-details modal. */
export default function useMetasearchPriceBreakdownCheck({ ready, surface, ...pricing }: Options) {
  const currency = useContext(CurrencyContext)
  const { query, pathname } = useAppSelector(state => state.router.location)
  const isVerifier = useAppSelector(isGhaVerifierUserAgent)
  const reported = useRef<string | undefined>(undefined)

  useEffect(() => {
    if (!ready || !isGhaRequest(query)) return

    const payload = {
      event: 'metasearch_price_breakdown_check',
      surface,
      visitorType: isVerifier ? 'verifier' : query.priceCheck === 'true' ? 'manual_verification' : 'customer',
      ...getMetasearchPriceBreakdownCheck(query, pricing, currency, pathname),
    }
    // React rerenders must not inflate the denominator. Changed settled prices get a fresh check.
    const key = JSON.stringify(payload)
    if (reported.current === key) return
    reported.current = key

    generalLog({
      status: payload.result === 'mismatch' ? 'warn' : 'info',
      message: 'Metasearch price breakdown check',
      payload,
    }).catch(noop)
  }, [ready, surface, pricing, currency, query, pathname, isVerifier])
}
